import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../components/common/Button'
import Spinner from '../components/common/Spinner'
import { LessonDonePill, LessonDoneTile } from '../components/learning/LessonDone'
import TeamActivity from '../components/social/TeamActivity'
import TeamCard from '../components/social/TeamCard'
import { clock } from '../lib/clock'
import { LEVEL_LABELS, continueTarget, greetingFor, remainingLine, suggestedLessons } from '../lib/home'
import { isLessonDone, lessonCardLook } from '../lib/lessonDone'
import { faDigits } from '../lib/numerals'
import { classifyFailure } from '../services/api'
import { listHistory, listLessons, myStats } from '../services/lessons'
import { myTeam, teamActivity } from '../services/teams'
import { useAuthStore } from '../store/authStore'

export function FlameGlyph({ size = 14, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={`fill-current ${className}`} aria-hidden="true">
      <path d="M12 2c1.5 4 5 5.5 5 9.5A5 5 0 0 1 7 11.5C7 7.5 10.5 6 12 2z" />
    </svg>
  )
}

function PlayGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" className="-ms-0.5 fill-current" aria-hidden="true">
      <path d="M8 5.2v13.6a1 1 0 0 0 1.53.85l10.2-6.8a1 1 0 0 0 0-1.7L9.53 4.35A1 1 0 0 0 8 5.2z" />
    </svg>
  )
}

function BookGlyph() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 5.5h7a2 2 0 0 1 2 2V20a2 2 0 0 0-2-2H3z" />
      <path d="M21 5.5h-7a2 2 0 0 0-2 2V20a2 2 0 0 1 2-2h7z" />
    </svg>
  )
}

/** The gold pill of the design's headers: the flame and the live streak from `me/stats`. */
export function StreakPill({ days }) {
  const { t } = useTranslation('home')
  return (
    <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-accent-tint px-3 py-2 text-accent-ink">
      <FlameGlyph className="text-accent" />
      <span className="text-xs font-bold">{t('streakDays', { days: faDigits(days) })}</span>
    </span>
  )
}

/**
 * The big card: the lesson `continueTarget` chose, at the sentence it
 * chose, with the accent bar under it for sentences attempted over the
 * lesson's count. It goes straight to practice — `?sentence=` asks the
 * lesson page for that very sentence, so the card and the screen it opens
 * say the same thing.
 */
export function ContinueCard({ target }) {
  const { t } = useTranslation('home')
  const { lesson, sentenceIndex, fresh, progress } = target
  return (
    <Link
      to={`/lesson/${lesson.id}?sentence=${sentenceIndex}`}
      className="block space-y-3.5 rounded-[20px] bg-linear-to-br from-hero-from to-hero-to p-[18px] text-white shadow-[0_6px_16px_rgba(43,90,150,0.22)] transition hover:brightness-105"
    >
      <span className="flex items-center gap-3">
        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-[11.5px] font-semibold text-white/75">{fresh ? t('continueCard.start') : t('continueCard.resume')}</span>
          <span dir="ltr" className="lesson-text block truncate text-right text-[19px] font-semibold">
            {lesson.titleEn}
          </span>
          <span className="text-[12.5px] text-white/75">
            {t('continueCard.sentenceOf', {
              current: faDigits(sentenceIndex + 1),
              total: faDigits(lesson.sentenceCount),
              level: LEVEL_LABELS[lesson.level] ?? lesson.level,
            })}
          </span>
        </span>
        <span className="grid size-[46px] shrink-0 place-items-center rounded-full bg-white text-brand-600">
          <PlayGlyph />
        </span>
      </span>
      <span
        className="block h-1.5 overflow-hidden rounded-full bg-white/30"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        aria-label={t('continueCard.progressAriaLabel')}
      >
        <span className="block h-full rounded-full bg-accent" style={{ width: `${Math.round(progress * 100)}%` }} />
      </span>
    </Link>
  )
}

function StatTile({ value, label, tone = 'text-ink' }) {
  return (
    <div className="flex flex-col items-center gap-1.5 card-soft rounded-tile px-2.5 py-3.5">
      <span className={`text-[19px] font-bold ${tone}`}>{value}</span>
      <span className="text-center text-[10.5px] text-ink-2">{label}</span>
    </div>
  )
}

export function SuggestionRow({ lesson }) {
  const done = isLessonDone(lesson)
  return (
    <li>
      <Link
        to={`/lesson/${lesson.id}`}
        data-lesson-done={done ? 'true' : undefined}
        className={`flex items-center gap-3 rounded-2xl p-3.5 transition ${lessonCardLook(done)} ${done ? 'hover:brightness-[0.98]' : 'hover:bg-surface-2'}`}
      >
        {done ? (
          <LessonDoneTile className="size-11 rounded-xl" glyph={20} />
        ) : (
          <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600">
            <BookGlyph />
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span dir="ltr" className="lesson-text block truncate text-right text-[15.5px] text-ink">
            {lesson.titleEn}
          </span>
          <span className="text-xs text-ink-2">
            {lesson.titleFa ? `${lesson.titleFa} · ` : ''}<span className="tnum" dir="ltr">{clock(lesson.durationSeconds)}</span>
          </span>
        </span>
        {done ? (
          <LessonDonePill />
        ) : (
          <span className="shrink-0 rounded-[9px] bg-surface-2 px-2.5 py-1.5 text-[11.5px] font-semibold text-ink-2">{LEVEL_LABELS[lesson.level] ?? lesson.level}</span>
        )}
      </Link>
    </li>
  )
}

/**
 * Home (design page 3, `home`; P-home of the go-live plan): the greeting,
 * the lessons left today, the streak, the card `lib/home.js` chose, three
 * tiles and two suggestions. All of it is real or absent — the catalogue,
 * `attempts/list` for the card, `me/stats` for the pill and the tiles —
 * and the three reads settle on their own, so a stats hiccup does not
 * blank the lessons. The header's bell is the shell's (P-notifs). The
 * feed is no longer a tab (V3-w), so a door to it closes the page until
 * V5 decides where it lives. The team card (V4-w) shows only once teams
 * are switched on (`teams_mine`'s `enabled`); until then it renders nothing.
 * So does the teammates' activity box above it (V5-w, `teams_activity`).
 */
export default function Home() {
  const { t } = useTranslation('home')
  const user = useAuthStore((s) => s.user)
  const [lessons, setLessons] = useState(null)
  const [history, setHistory] = useState(null)
  const [stats, setStats] = useState(null)
  const [team, setTeam] = useState(null)
  const [activity, setActivity] = useState(null)
  const [error, setError] = useState(null)
  const [reload, setReload] = useState(0)

  // Coming to the page re-reads the profile (the XP tile; throttled in the store), as the Android app does.
  useEffect(() => {
    void useAuthStore.getState().refreshProfile()
  }, [])

  useEffect(() => {
    let cancelled = false
    setError(null)
    listLessons()
      .then((data) => {
        if (!cancelled) setLessons(data.items)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    listHistory()
      .then((data) => {
        if (!cancelled) setHistory(data.items)
      })
      .catch(() => {
        // Without the history the card cannot be trusted; the page still stands.
        if (!cancelled) setHistory(false)
      })
    myStats()
      .then((data) => {
        if (!cancelled) setStats(data)
      })
      .catch(() => {})
    // V4: the team card is extra, like the stats — a failure hides it and nothing else.
    myTeam()
      .then((data) => {
        if (!cancelled) setTeam(data)
      })
      .catch(() => {})
    // V5: the teammates' day — extra too; empty while teams are off, and then nothing shows.
    teamActivity()
      .then((data) => {
        if (!cancelled) setActivity(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [reload])

  const target = lessons && Array.isArray(history) ? continueTarget(lessons, history) : null
  const suggestions = lessons ? suggestedLessons(lessons, target?.lesson) : []

  return (
    <div className="mx-auto max-w-md space-y-3.5">
      <header className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="truncate text-[21px] font-bold tracking-[-0.3px] text-ink">{greetingFor(user)}</h1>
          <p className="text-[13px] text-ink-2">{lessons ? remainingLine(lessons) : ' '}</p>
        </div>
        {stats && <StreakPill days={stats.streakCurrent ?? 0} />}
      </header>

      {error && !lessons ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${classifyFailure(error) === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {classifyFailure(error) === 'offline' ? t('errors.offline') : t('errors.server')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('errors.retry')}
          </Button>
        </div>
      ) : !lessons ? (
        <Spinner label={t('loadingLabel')} />
      ) : (
        <>
          {target && <ContinueCard target={target} />}

          <div className="grid grid-cols-3 gap-2">
            <StatTile value={stats ? faDigits(stats.attemptCount ?? 0) : '—'} label={t('stats.attempts')} />
            <StatTile value={faDigits(user?.xp ?? 0)} label={t('stats.xp')} tone="text-good" />
            <StatTile value={stats ? faDigits(stats.totalMinutes ?? 0) : '—'} label={t('stats.minutes')} />
          </div>

          <div className="flex items-center justify-between pt-1">
            <h2 className="text-[15px] font-bold text-ink">{t('suggestions.heading')}</h2>
            {/* «همهٔ درس‌ها» means the flat catalogue, which moved under the
                staged path in V7-w — the label stays honest by following it. */}
            <Link to="/lessons/all" className="text-[12.5px] font-semibold text-brand-600 hover:underline">
              {t('suggestions.allLessons')}
            </Link>
          </div>

          {suggestions.length === 0 ? (
            <p className="card-soft p-4 text-[12.5px] leading-[1.9] text-ink-2">{t('suggestions.empty')}</p>
          ) : (
            <ul className="space-y-2">
              {suggestions.map((lesson) => (
                <SuggestionRow key={lesson.id} lesson={lesson} />
              ))}
            </ul>
          )}
        </>
      )}

      <TeamActivity answer={activity} />

      <TeamCard answer={team} compact />

      <Link to="/social" className="flex h-14 items-center justify-between card-soft rounded-tile px-4 text-sm font-medium text-ink transition hover:bg-surface-2">
        <span>{t('feedCard.label')}</span>
        <span className="text-xs text-ink-3">{t('feedCard.hint')}</span>
      </Link>
    </div>
  )
}
